import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { useMutation } from '@apollo/client';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import FlagLabel from 'components/FlagLabel';
import iconClose from 'public/images/close.svg';
import { removeQueryParams } from 'src/utils/utils';
import { updateAuthDataInCookies } from 'src/utils/authUtils';
import { RESET_PASSWORD } from 'apolloClient/mutations/auth';
import { AuthenticatedUserInfo } from 'apolloClient/types';

import { useAuth } from './AuthProvider';

interface PopUpNewPasswordProps {}

const initialFormData = {
  Password: '',
  RepeatPassword: '',
};

const PopUpNewPassword: React.FC<PopUpNewPasswordProps> = ({}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const router = useRouter();
  const [formData, setFormData] = useState(initialFormData);
  const [sendResetPwdRequest, resetPwdRequestDetails] =
    useMutation(RESET_PASSWORD);
  const { onLoggedIn } = useAuth();

  // The resolver hands back a full { jwt, user } — the same shape login and
  // register return — so the caller is already authenticated. Make it stick
  // instead of telling them to go and log in again. This matters most for
  // accounts that never had a password (contact-us, Google, admin-created):
  // this link is how they get in, and stopping at "now log in" drops them right
  // back at the modal they just escaped.
  const logIn = (session: AuthenticatedUserInfo) => {
    if (!session.jwt || !session.user?.id) return false;

    updateAuthDataInCookies(session);
    onLoggedIn && onLoggedIn(session);

    return true;
  };

  // This modal is opened by the emailed link, so the link is the only context it
  // has. setUpPassword.html appends `setup=1`; forgotPassword.html does not, so
  // an existing reset link keeps behaving exactly as before.
  const isSetUp = router.query.setup === '1';

  const close = () => {
    setIsOpen(false);
    removeQueryParams(['rpToken', 'email', 'setup'], router);
  };

  useEffect(() => {
    if (router.query.rpToken && router.query.email) {
      setIsOpen(true);
      setFormData(initialFormData);
    }
  }, [router]);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSubmitted(true);

    if (
      formData.Password &&
      formData.Password === formData.RepeatPassword &&
      formData.Password.length >= 6
    ) {
      sendResetPwdRequest({
        variables: {
          code: router.query.rpToken,
          password: formData.Password,
          passwordConfirmation: formData.RepeatPassword,
        },
      })
        .then((res) => {
          // Apollo puts GraphQL errors on `res.errors`, not `res.data.errors`,
          // so the old check never matched and every failed reset reported
          // success. Require the jwt the resolver returns on a real reset —
          // for an account with no password this flow is the only way in, so a
          // false "success" here is a silent permanent lockout.
          const session = res?.data?.resetPassword as
            | AuthenticatedUserInfo
            | undefined;

          if (res?.errors?.length || !session?.jwt) {
            setIsCodeInvalid(true);
          } else {
            // Password IS set at this point even if the session could not be
            // established, so fall back to the "now log in" message rather than
            // claiming the reset failed.
            setIsLoggedIn(logIn(session));
            setIsSuccess(true);
          }
        })
        .catch((err) => setIsCodeInvalid(true));
    }
  };

  const onPwdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      Password: e.target.value,
    });
  };

  const onRepeatPwdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      RepeatPassword: e.target.value,
    });
  };

  return (
    <Dialog open={isOpen} onClose={close}>
      <div className="h-screen p-10 bg-white border border-gray md:h-full md:w-full">
        <div className="flex flex-row items-start justify-between mb-6">
          <div className="relative flex flex-col justify-center w-max">
            <FlagLabel name={isSetUp ? 'Set up password' : 'Reset password'} />
          </div>
          <button className="z-50 outline-none" onClick={close}>
            <Image src={iconClose} alt="close popup" width={15} height={15} />
          </button>
        </div>
        {!isSubmitted || !isSuccess ? (
          <div className="w-auto md:w-[420px]">
            <div className="mb-4">
              {isSetUp ? 'Choose a password for' : 'Set new password for'}{' '}
              <span className="font-sohneKraftig">{router.query.email}</span>
            </div>
            <form
              className="flex flex-col justify-center w-auto mt-8 text-16-26-0.3 align-center md:w-[420px]"
              onSubmit={onFormSubmit}
            >
              {/* sr-only, not hidden: Safari excludes display:none fields
                  from its password-save heuristic (Chrome does not). */}
              <input
                className="sr-only"
                type="email"
                name="Email"
                autoComplete="username"
                value={
                  typeof router.query.email === 'string'
                    ? router.query.email.trim()
                    : ''
                }
                readOnly
                tabIndex={-1}
                aria-hidden
              />
              <input
                className="text-16-26-0.3 border form-input"
                type="password"
                placeholder="New password"
                name="Password"
                autoComplete="new-password"
                value={formData.Password}
                onChange={onPwdChange}
              />
              <input
                className="text-16-26-0.3 border form-input mt-5 mb-4"
                type="password"
                placeholder="Re-enter new password"
                name="RepeatPassword"
                autoComplete="new-password"
                value={formData.RepeatPassword}
                onChange={onRepeatPwdChange}
              />
              {isSubmitted &&
                (!formData.Password || formData.Password.length < 6 ? (
                  <span className="field-validation-error">
                    <span className="pl-3 text-red">
                      Password is required and should be at least 6 characters
                      long
                    </span>
                  </span>
                ) : formData.Password !== formData.RepeatPassword ? (
                  <span className="field-validation-error">
                    <span className="pl-3 text-red">
                      Repeat password should be the same
                    </span>
                  </span>
                ) : (
                  isCodeInvalid && (
                    <span className="field-validation-error">
                      <span className="pl-3 text-red">
                        Password reset failed. The key is invalid or expired.
                      </span>
                    </span>
                  )
                ))}
              <div className="flex justify-between mt-6 text-12-18-0.3 md:text-16-26-0.3">
                <button
                  className="w-32 h-10 pt-1 uppercase bg-white border border-gold hover:bg-gold hover:border-gold"
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  className="w-32 h-10 pt-1 uppercase bg-gold hover:bg-gold-hover"
                  type="submit"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            Password for{' '}
            <span className="font-sohneKraftig">{router.query.email}</span> was
            successfully saved. Now you can close this dialog and use your new
            password to log in.
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PopUpNewPassword;
